import React, { useState, useEffect, useRef } from 'react';
import { round3 } from '../../utils/money';
import { IcSearch, IcPlus, IcPencil, IcTrash, IcBanknote, IcPrinter, IcFilters, IcClose, IcMoreHorizontal, IcReceiptText, IcCheckCircle, IcAlertCircle, IcCalendar } from '../../theme/icons';
import { Link, useNavigate } from 'react-router-dom';
import factureService from '../../services/factureService';
import paymentService from '../../services/paymentService';
import supplierService from '../../services/supplierService';
import authService from '../../services/authService';
import Swal from '../../theme/swal';
import KI from '../../theme/palette';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { CardSkeleton } from '../../components/Skeleton';
import { fold, filterSearch, matchesSearch } from '../../utils/search';

// Pilules de statut du panneau Filtres — même design / mêmes mots que
// l'Historique des ventes (owner 2026-09-09).
const STATUS_PILLS = [
  { value: 'all', label: 'Toutes', Icon: null },
  { value: 'paid', label: 'Payées', Icon: IcCheckCircle },
  { value: 'unpaid', label: 'Non payées', Icon: IcAlertCircle },
];

const ListFacturation = () => {
  const navigate = useNavigate();
  // Sellers may view the invoice list but not its financials (paid/unpaid status,
  // amounts) nor the edit/pay/delete actions — only printing. Manager/admin: full.
  const canManage = authService.isManagerOrAdmin();
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentFacture, setCurrentFacture] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');

  // Stats state
  const [statistics, setStatistics] = useState({ totalAmount: 0, paidAmount: 0, unpaidAmount: 0 });

  // Toolbar / client-side view state
  const [searchTerm, setSearchTerm] = useState('');
  const [quickStatus, setQuickStatus] = useState('all'); // 'all' | 'paid' | 'unpaid'
  const [showFilters, setShowFilters] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Supplier autocomplete + acompte
  const [pickedSupplier, setPickedSupplier] = useState(null);
  const [showSupplierSuggest, setShowSupplierSuggest] = useState(false);
  const [activeSuggest, setActiveSuggest] = useState(-1); // keyboard-highlighted suggestion
  const [acompte, setAcompte] = useState('');
  const [applyingAcompte, setApplyingAcompte] = useState(false);
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [acompteError, setAcompteError] = useState(null);
  const activeItemRef = useRef(null);

  // Server filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Période courante → paramètres serveur (vide tant que les 2 dates ne sont pas posées).
  const dateFilters = () => (startDate && endDate
    ? { start_date: format(startDate, 'yyyy-MM-dd'), end_date: format(endDate, 'yyyy-MM-dd') }
    : {});

  // Supplier list (filter dropdown)
  const [suppliers, setSuppliers] = useState([]);

  // Fetch factures
  const fetchFactures = async (filters = dateFilters()) => {
    setLoading(true);
    try {
      const data = await factureService.getAllFactures(filters);
      const factureArray = Array.isArray(data) ? data : [];
      setFactures(factureArray);
      calculateStatistics(factureArray);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des factures');
      console.error(err);
      setFactures([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtres « live » (comme l'Historique des ventes) : le statut se filtre
  // côté client (quickStatus), la période refetch dès que les 2 dates sont
  // posées ; Réinitialiser = lien rouge à côté du bouton Filtres.
  const hasActiveFilters = quickStatus !== 'all' || !!(startDate || endDate);
  const panelFilterCount = (quickStatus !== 'all' ? 1 : 0) + (startDate && endDate ? 1 : 0);
  const clearFilters = () => {
    setQuickStatus('all');
    setStartDate(null);
    setEndDate(null);
  };

  // Calculate statistics
  const calculateStatistics = (factureData) => {
    const stats = factureData.reduce((acc, facture) => {
      const total = Number(facture.total) || 0;
      const amountDue = Number(facture.amount_due) || 0;
      // Un avoir (retour fournisseur) n'est pas « payé » : son total négatif
      // se déduit du total des factures, rien d'autre.
      const paid = facture.is_avoir ? 0 : total - amountDue;
      return {
        totalAmount: acc.totalAmount + total,
        paidAmount: acc.paidAmount + paid,
        unpaidAmount: acc.unpaidAmount + amountDue,
      };
    }, { totalAmount: 0, paidAmount: 0, unpaidAmount: 0 });
    setStatistics(stats);
  };

  // Payment modal
  const handlePayment = (facture) => {
    setCurrentFacture(facture);
    setPaymentAmount('');
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentAmount || isNaN(paymentAmount) || parseFloat(paymentAmount) <= 0) {
      Swal.fire('Erreur', 'Veuillez entrer un montant valide', 'error');
      return;
    }
    const amount = round3(parseFloat(paymentAmount));
    if (amount > currentFacture.amount_due) {
      Swal.fire('Erreur', 'Le montant ne peut pas dépasser le montant dû', 'error');
      return;
    }
    try {
      await factureService.updateFacturePayment(currentFacture.id, { amount });
      setShowPaymentModal(false);
      Swal.fire('Succès', 'Paiement enregistré avec succès', 'success');
      fetchFactures();
    } catch (err) {
      Swal.fire('Erreur', "Erreur lors de l'enregistrement du paiement", 'error');
      console.error(err);
    }
  };

  // Acompte (distributed across the picked supplier's unpaid factures, oldest first)
  // — same modal design & flow as the supplier detail page.
  const handleOpenAcompteModal = () => {
    setAcompte('');
    setAcompteError(null);
    setShowAcompteModal(true);
  };

  const handleApplyAcompte = async (e) => {
    e.preventDefault();
    const amount = round3(parseFloat(acompte));
    if (!amount || isNaN(amount) || amount <= 0) {
      setAcompteError('Veuillez entrer un montant valide.');
      return;
    }
    if (amount > supplierUnpaidTotal + 1e-6) {
      setAcompteError('Le montant dépasse le reste à payer du fournisseur.');
      return;
    }
    const unpaid = supplierFactures
      .filter((f) => (Number(f.amount_due) || 0) > 0)
      .sort((a, b) => new Date(a.date || a.date_added || 0) - new Date(b.date || b.date_added || 0));

    setApplyingAcompte(true);
    setAcompteError(null);
    const EPS = 1e-6;
    // If the user is paying (essentially) the whole balance, clear it exactly so no
    // sub-millime float dust is left behind on the supplier.
    const payAll = amount >= supplierUnpaidTotal - 5e-4;
    let remaining = payAll ? supplierUnpaidTotal : amount;
    let totalPaid = 0;
    try {
      for (const f of unpaid) {
        if (remaining <= EPS) break;
        const due = Number(f.amount_due) || 0;
        if (due <= 0) continue;
        // Pay the facture in full when the remaining covers it — send its EXACT
        // amount_due so we never round above it (the backend rejects amount > due,
        // and stored dues can carry >3 decimals from float arithmetic).
        const pay = remaining >= due - EPS ? due : remaining;
        if (pay <= 0) continue;
        await factureService.updateFacturePayment(f.id, { amount: pay, skip_payment_log: true });
        remaining -= pay;
        totalPaid += pay;
      }
      // Record the acompte as one dated payment in the supplier's payment history.
      if (totalPaid > EPS && pickedSupplier?.id) {
        try {
          await paymentService.create({
            payment_type: 'supplier',
            supplier: pickedSupplier.id,
            source: 'acompte',
            amount: round3(totalPaid),
            note: 'Acompte sur factures',
          });
        } catch (payErr) {
          // Don't fail the acompte if only the history record couldn't be written.
          console.error('Acompte recorded on factures but payment log failed:', payErr);
        }
      }
      setShowAcompteModal(false);
      setAcompte('');
      fetchFactures();
    } catch (err) {
      console.error('Acompte error:', err);
      setAcompteError("Erreur lors de l'enregistrement de l'acompte.");
    } finally {
      setApplyingAcompte(false);
    }
  };

  // Status meta (label + colors)
  const getStatusMeta = (facture) => {
    const amountDue = Number(facture.amount_due) || 0;
    const total = Number(facture.total) || 0;
    // Etiquettes-2 §3 : symbole + libellé ; barre #FECACA (red-200) pour le
    // partiel, verte à 100 %.
    if (facture.is_avoir) {
      return { key: 'avoir', label: '↩ Avoir', pill: 'bdg-warn', bar: 'bg-gray-200' };
    }
    if (amountDue <= 0) {
      return { key: 'paid', label: '✓ Payé', pill: 'bdg-pos', bar: 'bg-green-700' };
    } else if (amountDue < total) {
      return { key: 'partial', label: '◐ Partiel', pill: 'bdg-alert', bar: 'bg-red-200' };
    }
    return { key: 'unpaid', label: '● Non payé', pill: 'bdg-critical', bar: 'bg-red-200' };
  };

  const handleGeneratePDF = async (factureId) => {
    try {
      await factureService.generatePDF(factureId);
    } catch (err) {
      Swal.fire('Erreur', 'Erreur lors de la génération du PDF', 'error');
      console.error(err);
    }
  };

  // Delete (SweetAlert, matches the rest of the app)
  const handleDeleteFacture = async (factureId) => {
    const result = await Swal.fire({
      title: 'Supprimer cette facture ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: KI.coralDark,
      cancelButtonColor: KI.faint,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (!result.isConfirmed) return;
    try {
      await factureService.deleteFacture(factureId);
      Swal.fire('Supprimée', 'La facture a été supprimée.', 'success');
      fetchFactures();
    } catch (err) {
      console.error('Error deleting facture:', err);
      Swal.fire('Erreur', 'Erreur lors de la suppression de la facture.', 'error');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await supplierService.getAllSuppliers();
      const suppliersData = Array.isArray(data) ? data : (data.results || []);
      setSuppliers(suppliersData);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  useEffect(() => { fetchSuppliers(); }, []);

  // Charge la liste au montage puis à chaque période complète (ou vidée).
  useEffect(() => {
    if ((startDate && endDate) || (!startDate && !endDate)) fetchFactures(dateFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Client-side derived list (search + quick status)
  // Règle Ki Para (utils/search.js) : fournisseur par début de mot,
  // n° / code-barres « contient ».
  const factureSupplierMatch = (f, sup) =>
    String(f.supplier) === String(sup.id) || fold(f.supplier_name) === fold(sup.name);

  const displayedFactures = factures.filter((f) => {
    if (quickStatus === 'paid' && ((Number(f.amount_due) || 0) > 0 || f.is_avoir)) return false;
    if (quickStatus === 'unpaid' && !((Number(f.amount_due) || 0) > 0)) return false;
    if (pickedSupplier) {
      if (!factureSupplierMatch(f, pickedSupplier)) return false;
    } else if (!matchesSearch(searchTerm, f.supplier_name, [f.barrecode, f.number])) {
      return false;
    }
    return true;
  });

  // Supplier suggestions for the search box
  const supplierSuggestions = (!pickedSupplier && searchTerm.trim())
    ? filterSearch(suppliers, searchTerm, { text: (s) => s.name, codes: (s) => [s.phone] }).slice(0, 8)
    : [];

  // Picked supplier's factures + outstanding total (for the acompte card)
  const supplierFactures = pickedSupplier ? factures.filter((f) => factureSupplierMatch(f, pickedSupplier)) : [];
  const supplierUnpaidTotal = supplierFactures.reduce((s, f) => s + (Number(f.amount_due) || 0), 0);

  // Pick a supplier from the suggestions (shared by mouse + keyboard)
  const chooseSupplier = (s) => {
    setPickedSupplier(s);
    setSearchTerm(s.name);
    setShowSupplierSuggest(false);
    setActiveSuggest(-1);
    setQuickStatus('all');
  };

  // Keyboard navigation for the supplier autocomplete (↓ ↑ Enter Échap)
  const handleSearchKeyDown = (e) => {
    if (!showSupplierSuggest || supplierSuggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggest((i) => Math.min(i + 1, supplierSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggest((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeSuggest >= 0 && activeSuggest < supplierSuggestions.length) {
        e.preventDefault();
        chooseSupplier(supplierSuggestions[activeSuggest]);
      }
    } else if (e.key === 'Escape') {
      setShowSupplierSuggest(false);
      setActiveSuggest(-1);
    }
  };

  // Keep the keyboard-highlighted suggestion scrolled into view
  useEffect(() => {
    if (activeItemRef.current) activeItemRef.current.scrollIntoView({ block: 'nearest' });
  }, [activeSuggest]);

  // Carte KPI (Composants-2 §5) : icône 16px en haut à droite, libellé,
  // valeur 26px tabulaire, sous-ligne. Owner Studio 2026-09-08 : les 3 cartes
  // sont blanches, icône + libellé gray-500, seule la VALEUR porte la couleur.
  // Owner 2026-09-09 : les cartes redeviennent INFORMATIVES (plus de clic =
  // filtre) — le filtre payé / non payé vit dans le panneau Filtres.
  const StatCard = ({ title, amount, count, accent }) => (
    <div className="kpi rounded-2xl">
      <accent.Icon size={16} className="kpi-icon text-gray-500" />
      <p className="kpi-label uppercase pr-6 mb-2">{title}</p>
      <p className={`kpi-value ${accent.value || ''}`}>{amount.toFixed(3)} DT</p>
      <p className={`kpi-sub mt-1 ${accent.sub || ''}`}>{count}</p>
    </div>
  );

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Page name as a gray chip — same design as the other pages
          (owner Studio); icon box, big title, Nouvelle facture removed. */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Chip = refresh button (owner Studio 2026-08-13; pages « ajouter » excluded) */}
        <button type="button" onClick={() => window.location.reload()} title="Actualiser la page" className="btn-s px-4 py-2 rounded-xl text-sm">
          <IcReceiptText size={13} /> Liste des factures
        </button>
      </div>

      {/* Toolbar: search + filter toggle — same as the seller-sales page (owner). */}
      <div className="flex gap-3 mb-3 items-center">
        <div className="relative flex-1">
          {/* Composants-2 §2 : 44px, icône search 16px #6B7280, liste
              d'autocomplete collée sous le champ (ligne active #F3F4F6,
              nom en gras, secondaire gris tabulaire). */}
          <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 z-10" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPickedSupplier(null); setShowSupplierSuggest(true); setActiveSuggest(-1); }}
            onFocus={() => setShowSupplierSuggest(true)}
            onBlur={() => setTimeout(() => setShowSupplierSuggest(false), 150)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Rechercher un fournisseur..."
            autoComplete="off"
            className="inp w-full pl-11 pr-10 py-2.5 rounded-xl text-base"
          />
          {(searchTerm || pickedSupplier) && (
            <button
              type="button"
              onClick={() => { setSearchTerm(''); setPickedSupplier(null); setAcompte(''); setShowSupplierSuggest(false); }}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              title="Effacer"
            >
              <IcClose />
            </button>
          )}
          {showSupplierSuggest && supplierSuggestions.length > 0 && (
            <div className="ac-list mt-2 rounded-xl py-1 max-h-72 overflow-auto">
              {supplierSuggestions.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  ref={i === activeSuggest ? activeItemRef : null}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => chooseSupplier(s)}
                  onMouseEnter={() => setActiveSuggest(i)}
                  className={`ac-row w-full px-4 text-left ${i === activeSuggest ? 'ac-row-on' : ''}`}
                >
                  <span className="ac-name text-sm">{s.name}</span>
                  {s.phone && <span className="ac-sub text-xs">{s.phone}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Composants-2 §3 : hors formulaire, Réinitialiser = lien rouge
            (même rangée que sur l'Historique des ventes). */}
        {hasActiveFilters && (
          <button type="button" onClick={clearFilters} className="link-reset gap-1.5 px-3 py-1.5 text-sm">
            Réinitialiser
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="btn-s relative h-12 w-[8.5rem] px-4 py-2.5 rounded-xl"
        >
          <IcFilters className="text-sm" /> Filtres
          {panelFilterCount > 0 && (
            <span className="bdg-count static ml-2">{panelFilterCount}</span>
          )}
        </button>
      </div>

      {/* Panneau Filtres — même design que l'Historique des ventes (owner
          2026-09-09) : rangée de pilules de statut (Toutes / Payées / Non
          payées) puis grille de champs `inp` (Date début / Date fin) ; carte
          .flt-card rayon 2xl, padding 4, gap 3. Plus de case « non payées
          seulement » ni d'Appliquer : tout se filtre en direct. */}
      {showFilters && (
        <div className="flt-card rounded-2xl p-4 mb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Statut">
            {STATUS_PILLS.map((st) => {
              const active = quickStatus === st.value;
              return (
                <button
                  key={st.value}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setQuickStatus(st.value)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors ${active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {st.Icon && <st.Icon className="text-xs" />} {st.label}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="flt-label" htmlFor="fl-filter-start">Date début</label>
              <div className="relative">
                <IcCalendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <DatePicker
                  id="fl-filter-start"
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  wrapperClassName="w-full"
                  className="inp w-full pl-9 pr-3 py-2.5 text-gray-900"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="jj/mm/aaaa"
                />
              </div>
            </div>
            <div>
              <label className="flt-label" htmlFor="fl-filter-end">Date fin</label>
              <div className="relative">
                <IcCalendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                <DatePicker
                  id="fl-filter-end"
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  wrapperClassName="w-full"
                  className="inp w-full pl-9 pr-3 py-2.5 text-gray-900"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="jj/mm/aaaa"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cartes KPI informatives — financier, admin seulement (le filtre
          payé / non payé est dans le panneau Filtres depuis le 2026-09-09) */}
      {canManage && (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          title="Total factures"
          amount={statistics.totalAmount}
          count={`${factures.filter((f) => !f.is_avoir).length} facture(s)${factures.some((f) => f.is_avoir) ? ` · ${factures.filter((f) => f.is_avoir).length} avoir(s)` : ''}`}
          accent={{ Icon: IcReceiptText }}
        />
        <StatCard
          title="Montant payé"
          amount={statistics.paidAmount}
          count={`${factures.filter((f) => !f.is_avoir && (Number(f.amount_due) || 0) <= 0).length} payée(s)`}
          accent={{ Icon: IcCheckCircle, value: 'text-green-900', sub: 'text-gray-500' }}
        />
        {pickedSupplier ? (
          <div className="kpi rounded-2xl kpi-coral cursor-default">
            <IcAlertCircle size={16} className="kpi-icon" />
            <p className="kpi-label uppercase truncate pr-6 mb-2">Non payé · {pickedSupplier.name}</p>
            <p className="kpi-value">{supplierUnpaidTotal.toFixed(3)} DT</p>
            {supplierUnpaidTotal > 0.0005 ? (
              <button
                onClick={handleOpenAcompteModal}
                className="mt-3 w-full flex items-center justify-center gap-2 text-sm py-2 btn-p transition-colors"
              >
                <IcBanknote /> Enregistrer un acompte
              </button>
            ) : (
              <p className="text-xs text-green-600 mt-2 font-medium">Tout est payé pour ce fournisseur</p>
            )}
          </div>
        ) : (
          <StatCard
            title="Montant non payé"
            amount={statistics.unpaidAmount}
            count={`${factures.filter((f) => (Number(f.amount_due) || 0) > 0).length} non payée(s)`}
            accent={{ Icon: IcAlertCircle, value: 'text-red-700', sub: 'text-gray-500' }}
          />
        )}
      </div>
      )}

      {/* Cards grid */}
      {loading && factures.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <CardSkeleton />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl" role="alert">
          {error}
        </div>
      ) : displayedFactures.length === 0 ? (
        /* État vide (Brand Kit §05) : cadre pointillé, icône 22px, phrase 13px 600 */
        <div className="empty-ki rounded-2xl p-12">
          <IcReceiptText className="empty-ki-icon w-9 h-9 text-4xl mb-3" />
          <p className="empty-ki-title text-base mt-0">Aucune facture trouvée</p>
          <p className="empty-ki-sub mb-0">Essayez une autre période ou retirez les filtres.</p>
        </div>
      ) : (
        <div className="overflow-y-auto pr-1 -mr-1 max-h-[calc(100vh-340px)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-1">
          {displayedFactures.map((facture) => {
            const meta = getStatusMeta(facture);
            const total = Number(facture.total) || 0;
            const amountDue = Number(facture.amount_due) || 0;
            const paid = total - amountDue;
            const pct = total > 0 ? Math.min(100, Math.max(0, (paid / total) * 100)) : (amountDue <= 0 ? 100 : 0);
            const totalQty = Array.isArray(facture.details)
              ? facture.details.reduce((s, d) => s + (Number(d.quantity) || 0), 0) || facture.details.length
              : 0;
            const code = facture.number || facture.barrecode || 'N/A';
            const dateStr = facture.date
              ? new Date(facture.date).toLocaleDateString('fr-FR')
              : (facture.date_added ? new Date(facture.date_added).toLocaleDateString('fr-FR') : '');

            return (
              <div
                key={facture.id || facture.barrecode}
                onClick={() => navigate(`/facturation/detail/${facture.id}`)}
                className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-col hover:border-gray-300 transition-all cursor-pointer"
              >
                {/* Top: code + status (status is financial → manager/admin only) */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-900">{code}</span>
                  {canManage && (
                    <span className={`bdg ${meta.pill}`}>{meta.label}</span>
                  )}
                </div>

                {/* Supplier + date */}
                <div className="mb-4">
                  <p className="font-semibold text-gray-800 leading-tight">{facture.supplier_name || 'N/A'}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{dateStr} · {totalQty} article(s)</p>
                </div>

                {/* Total + payment progress — financial, manager/admin only */}
                {canManage && (
                  <>
                    <div className="flex items-end justify-between mb-3">
                      <span className="text-xs text-gray-500">Total</span>
                      <span className="text-xl font-bold text-gray-900">{total.toFixed(3)} DT</span>
                    </div>

                    <div className="mb-4">
                      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="flex justify-between text-xs mt-1.5">
                        <span className="text-gray-500">Payé {paid.toFixed(3)}</span>
                        {amountDue > 0
                          ? <span className="text-red-600 font-medium">Reste {amountDue.toFixed(3)}</span>
                          : <span className="text-green-600 font-medium">Soldée</span>}
                      </div>
                    </div>
                  </>
                )}

                {/* Actions (don't trigger the card's navigation). Sellers: print only. */}
                <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5 mt-auto pt-3 border-t border-gray-100">
                  {canManage && (
                    <Link to={`/facturation/edit/${facture.id}`} title="Modifier" className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 transition-colors">
                      <IcPencil />
                    </Link>
                  )}
                  <button onClick={() => handleGeneratePDF(facture.id)} title="Imprimer" className="btn-s h-9 w-9 transition-colors">
                    <IcPrinter />
                  </button>

                  {canManage && amountDue > 0 && (
                    <button
                      onClick={() => handlePayment(facture)}
                      className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs btn-p transition-colors"
                    >
                      <IcBanknote /> Payer
                    </button>
                  )}

                  {canManage && (
                    <div className={`relative ${amountDue > 0 ? '' : 'ml-auto'}`}>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === facture.id ? null : facture.id)}
                        title="Plus"
                        className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                      >
                        <IcMoreHorizontal />
                      </button>
                      {openMenuId === facture.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                          <div className="absolute right-0 bottom-full mb-2 z-20 bg-white rounded-xl shadow-lg border border-gray-100 py-1 w-40">
                            <button
                              onClick={() => { setOpenMenuId(null); handleDeleteFacture(facture.id); }}
                              className="w-full flex items-center gap-2 px-4 py-2 text-sm btn-db"
                            >
                              <IcTrash className="text-xs" /> Supprimer
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}

      {/* Payment Modal — matches the facture page modal style */}
      {showPaymentModal && currentFacture && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={() => setShowPaymentModal(false)}>
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head px-6">
              <h2 className="modal-title text-lg">{currentFacture.supplier_name || 'Enregistrer un paiement'}</h2>
              <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-white"><IcClose /></button>
            </div>
            <div className="p-6">
            <p className="text-sm text-gray-500 mb-5">
              Facture {currentFacture.barrecode || currentFacture.number || ''}
            </p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Total</span>
                <span className="font-medium text-gray-800">{Number(currentFacture.total).toFixed(3)} DT</span>
              </div>
              {Number(currentFacture.timbre) > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-400 text-xs">dont timbre fiscal (hors TVA)</span>
                  <span className="text-xs text-gray-500">{Number(currentFacture.timbre).toFixed(3)} DT</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-100">
                <span className="font-semibold text-gray-700">Reste à payer</span>
                <span className="text-lg font-bold text-red-600">{Number(currentFacture.amount_due).toFixed(3)} DT</span>
              </div>
            </div>

            <form onSubmit={handlePaymentSubmit}>
              <div className="mt-5">
                <label className="block text-sm font-medium text-gray-600 mb-1.5">Montant à payer</label>
                <div className="relative">
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    min="0"
                    max={currentFacture.amount_due}
                    step="0.001"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900"
                    placeholder="0.000"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">DT</span>
                </div>
              </div>

              {/* Composants-2 §10 : pied à droite — Annuler ghost + Confirmer primaire vert (.btn-p, owner 2026-09-10) */}
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => setShowPaymentModal(false)} className="btn-g px-4 py-2.5">Annuler</button>
                <button
                  type="submit"
                  className="px-6 py-2.5 btn-p transition-colors"
                >
                  Confirmer le paiement
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Acompte Modal — same design & flow as the supplier detail page */}
      {showAcompteModal && pickedSupplier && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={() => setShowAcompteModal(false)}>
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head px-6">
              <h2 className="modal-title text-lg">Enregistrer un acompte</h2>
              <button onClick={() => setShowAcompteModal(false)} className="text-gray-400 hover:text-white"><IcClose /></button>
            </div>
            <div className="p-6">
            <p className="text-sm text-gray-500 mb-5">{pickedSupplier.name}</p>

            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Reste à payer (total)</span>
                <span className="font-semibold text-red-600">{supplierUnpaidTotal.toFixed(3)} DT</span>
              </div>
              <p className="text-xs text-gray-400 pt-1">
                L'acompte est appliqué aux factures impayées, des plus anciennes aux plus récentes.
              </p>
            </div>

            <form onSubmit={handleApplyAcompte}>
              {acompteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm mt-4">{acompteError}</div>
              )}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-600 mb-1.5">Montant de l'acompte</label>
                <div className="relative">
                  <input
                    type="number"
                    value={acompte}
                    onChange={(e) => setAcompte(e.target.value)}
                    min="0"
                    max={supplierUnpaidTotal}
                    step="0.001"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900"
                    placeholder="0.000"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">DT</span>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button type="button" onClick={() => setShowAcompteModal(false)} className="btn-g px-4 py-2.5">Annuler</button>
                <button
                  type="submit"
                  disabled={applyingAcompte}
                  className="px-6 py-2.5 btn-p transition-colors"
                >
                  {applyingAcompte ? 'Enregistrement...' : "Confirmer l'acompte"}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListFacturation;
